import {
  ForwardedRef,
  forwardRef,
  useImperativeHandle,
  useCallback,
  useRef,
  useState,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  INTERVIEW_CONDITIONS_LIST,
  INTERVIEW_TYPE,
  INTERVIEW_TYPE_STR,
  InterviewEntity,
  useCreateInterviewMutation,
  useUpdateInterviewMutation,
  useDeleteInterviewMutation,
  LlmModelSize,
  INTERVIEW_CONDITIONS,
} from 'src/store/interviewApi';
import {
  selectInterviewCount,
  setInterview as setInterviewAction,
} from 'src/store/interviewSlice';
import _ from 'lodash';
import ProfileFieldSelector from 'src/component/profileFiledSelector';
import { selectOptions } from 'src/store/optionSlice';
import { OptionEntity } from 'src/store/optionApi';
import { Panel } from 'src/ui/Panel';
import { Button } from 'src/ui/Button';
import { Input } from 'src/ui/Input';
import { Textarea } from 'src/ui/Textarea';
import { Select } from 'src/ui/Select';
import { Field } from 'src/ui/Field';
import { Switch } from 'src/ui/Switch';
import { Icon } from 'src/ui/Icon';
import { Dropdown, MenuItem } from 'src/ui/Dropdown';
import { useToast } from 'src/ui/Toast';

export interface EditorRef {
  onCreate: () => void;
  onSelect: (dbInterview: InterviewEntity) => void;
}

const blankInterview = (interviewCount: number): InterviewEntity => ({
  id: 0,
  sequence: interviewCount + 1,
  name: '',
  type: INTERVIEW_TYPE.AI,
  prefillAnswers: [],
  condition: {
    type: INTERVIEW_CONDITIONS.always,
  },
  question: {},
  questionConfig: {},
  collect: [],
  disabled: false,
  placeholder: {},
  sectionId: 0,
});

export interface EditorProps {
  refetchAll: () => void;
  onTestInterview: (interviewId: number) => void;
}

const Editor = forwardRef(
  (
    { refetchAll, onTestInterview }: EditorProps,
    ref: ForwardedRef<EditorRef>
  ) => {
    const dispatch = useDispatch();
    const toast = useToast();
    const interviewCount = useSelector(selectInterviewCount);
    const [interview, setInterview] = useState<InterviewEntity>(
      blankInterview(interviewCount)
    );
    const optionEntities = useSelector(selectOptions);
    const isDirty = useRef(false);
    const language = 'cn';
    const [errors, setErrors] = useState<Record<string, string>>({});

    const [createInterview, { isLoading: isCreating }] =
      useCreateInterviewMutation();
    const [updateInterview, { isLoading: isUpdating }] =
      useUpdateInterviewMutation();
    const [deleteInterview, { isLoading: isDeleting }] =
      useDeleteInterviewMutation();

    const checkCanChange = useCallback(() => {
      if (isDirty.current) {
        const confirmed = window.confirm(
          'You have unsaved changes. Are you sure you want to discard them?'
        );
        if (confirmed) {
          isDirty.current = false;
          return true;
        }
        return false;
      }
      return true;
    }, []);

    const onCreate = () => {
      if (checkCanChange()) {
        setInterview(blankInterview(interviewCount));
      }
    };

    const onDuplicate = () => {
      setInterview({
        ...interview,
        id: 0,
        sequence: interviewCount + 1,
        name: `${interview.name}_copy`,
      });
    };

    useImperativeHandle(ref, () => ({
      onCreate,
      onSelect: (dbInterview: InterviewEntity) => {
        if (checkCanChange()) {
          setInterview(_.cloneDeep(dbInterview));
        }
      },
    }));

    const onChange = (
      key: string,
      value: string | number | boolean | undefined
    ) => {
      isDirty.current = true;
      const newInterview: any = { ...interview };
      const keys = key.split('.');
      if (keys.length === 1) {
        newInterview[key] = value;
      } else {
        newInterview[keys[0]][keys[1]] = value;
      }
      setInterview(newInterview);
    };

    const onBulkChange = (payload: Partial<InterviewEntity>) => {
      isDirty.current = true;
      setInterview({ ...interview, ...payload });
    };

    const onChangePrefill = (idx: number, value: string) => {
      isDirty.current = true;
      const newInterview: any = { ...interview };
      if (!newInterview.prefillAnswers) newInterview.prefillAnswers = [];
      if (!newInterview.prefillAnswers[idx])
        newInterview.prefillAnswers[idx] = {};
      newInterview.prefillAnswers[idx][language] = value;
      setInterview(newInterview);
    };

    const onDelelePrefill = (idx: number) => {
      isDirty.current = true;
      const newInterview: any = { ...interview };
      if (!newInterview.prefillAnswers) newInterview.prefillAnswers = [];
      newInterview.prefillAnswers.splice(idx, 1);
      setInterview(newInterview);
    };

    const onChangeCollect = (id: number, key: string, value = '') => {
      isDirty.current = true;
      const newInterview: any = { ...interview };
      if (!newInterview.collect) newInterview.collect = [];
      if (!newInterview.collect[id]) newInterview.collect[id] = [key, {}];
      const values = { ...newInterview.collect[id][1] };
      values[language] = value;
      newInterview.collect[id] = [key, values];
      setInterview(newInterview);
    };

    const onDeleteCollect = (id: number) => {
      isDirty.current = true;
      const newInterview: any = { ...interview };
      if (!newInterview.collect) newInterview.collect = [];
      newInterview.collect.splice(id, 1);
      setInterview(newInterview);
    };

    const onSave = async () => {
      if (!interview) return;
      const newErros: Record<string, string> = {};
      if (!interview.name) newErros.name = 'Name is required';
      if (Object.keys(newErros).length > 0) {
        setErrors(newErros);
        return;
      }
      setErrors({});
      try {
        let newInterview;
        if (interview.id) {
          newInterview = await updateInterview(interview).unwrap();
        } else {
          newInterview = await createInterview(interview).unwrap();
        }
        setInterview(_.cloneDeep(newInterview.record));
        dispatch(setInterviewAction(newInterview.record));
        isDirty.current = false;
        toast({ type: 'success', title: 'Interview saved' });
        if (newInterview.needReloadAll) refetchAll();
      } catch (e: any) {
        toast({
          type: 'error',
          title: 'Failed to save interview',
          message: e.message || e.error,
        });
      }
    };

    const onDelete = async () => {
      await deleteInterview(interview.id).unwrap();
      toast({ type: 'success', title: 'Interview deleted' });
      refetchAll();
    };

    /* ---- Condition ---- */
    const renderCondition = () => {
      const { field, type, value } = interview.condition;
      const optionEntity = Object.values(optionEntities).find(
        (o) => o.field === field
      );

      const onSelectValue = (v: number) => {
        const mask = 1 << v;
        if (['contains', 'notContains'].includes(type || '')) {
          let newValue = value || 0;
          if (newValue & mask) newValue -= mask;
          else newValue |= mask;
          onChange('condition.value', newValue);
        } else {
          onChange('condition.value', mask);
        }
      };

      let decodedValue = typeof value === 'number' ? String(value) : '';
      if (optionEntity) {
        const mask = value || 0;
        const enabledLabels = optionEntity.labels?.filter(
          ([k]) => (mask & (1 << k)) > 0
        );
        decodedValue = enabledLabels
          .map((label) => label[1][language])
          .join(', ');
      }

      return (
        <div className="col gap-2">
          <div
            className="row gap-4"
            style={{ justifyContent: 'space-between', alignItems: 'center' }}
          >
            <label className="field-label" style={{ margin: 0 }}>
              Condition (show when):
            </label>
            <Dropdown
              trigger={
                <Button variant="secondary" size="sm" rightIcon="chevron-down">
                  {type || 'always'}
                </Button>
              }
              align="right"
              width={160}
            >
              {INTERVIEW_CONDITIONS_LIST.map((con) => (
                <MenuItem
                  key={con}
                  active={con === type}
                  onClick={() => onChange('condition.type', con)}
                >
                  {con}
                </MenuItem>
              ))}
            </Dropdown>
          </div>
          {type !== 'always' && (
            <div
              className="row gap-2"
              style={{ alignItems: 'center', flexWrap: 'wrap' }}
            >
              <ProfileFieldSelector
                value={field || ''}
                onChange={(val: string) => onChange('condition.field', val)}
              />
              <span className="text-muted" style={{ fontSize: 'var(--fs-sm)' }}>
                {type}
              </span>
              {optionEntity ? (
                <Dropdown
                  trigger={
                    <Button
                      variant="secondary"
                      size="sm"
                      rightIcon="chevron-down"
                    >
                      {decodedValue || 'Select value'}
                    </Button>
                  }
                  align="right"
                  width={200}
                >
                  {optionEntity.labels?.map(([k, v]) => (
                    <MenuItem key={k} onClick={() => onSelectValue(k)}>
                      {v[language]}
                    </MenuItem>
                  ))}
                </Dropdown>
              ) : (
                <Input
                  size="sm"
                  style={{ width: 100 }}
                  value={value || ''}
                  type="number"
                  onChange={(e) =>
                    onChange('condition.value', Number(e.target.value))
                  }
                />
              )}
            </div>
          )}
        </div>
      );
    };

    /* ---- Collect row ---- */
    const renderCollect = (
      [key, value]: [string, Record<string, string> | undefined],
      idx: number
    ) => (
      <div key={idx} className="col gap-2">
        <div className="row gap-2" style={{ alignItems: 'center' }}>
          <ProfileFieldSelector
            value={key}
            onChange={(val) => onChangeCollect(idx, val, value?.[language])}
            answersPrefix={interview.name}
          />
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            title="Delete"
            onClick={() => onDeleteCollect(idx)}
          >
            <Icon name="trash" size={14} />
          </Button>
        </div>
        <Textarea
          value={value?.[language] || ''}
          onChange={(e) => onChangeCollect(idx, key, e.target.value)}
          placeholder="Description of what to collect. E.g. User's love experience."
        />
      </div>
    );

    /* ---- AI config ---- */
    const renderAiConfig = () => (
      <div className="col gap-4" style={{ marginTop: 4 }}>
        <div
          className="row gap-4"
          style={{ justifyContent: 'space-between', alignItems: 'center' }}
        >
          <label className="field-label" style={{ margin: 0 }}>
            LLM Model
          </label>
          <Dropdown
            trigger={
              <Button variant="secondary" size="sm" rightIcon="chevron-down">
                {interview.questionConfig.llmModal || LlmModelSize.ADV}
              </Button>
            }
            align="right"
            width={200}
          >
            <MenuItem
              active={
                (interview.questionConfig.llmModal || LlmModelSize.ADV) ===
                LlmModelSize.ADV
              }
              onClick={() =>
                onChange('questionConfig.llmModal', LlmModelSize.ADV)
              }
            >
              Advanced (gpt-4o)
            </MenuItem>
            <MenuItem
              active={interview.questionConfig.llmModal === LlmModelSize.ECO}
              onClick={() =>
                onChange('questionConfig.llmModal', LlmModelSize.ECO)
              }
            >
              Economic (gpt-4o-mini)
            </MenuItem>
          </Dropdown>
        </div>
        <Field label="Additional Prompt">
          <Textarea
            value={interview.questionConfig.additionalPrompt || ''}
            onChange={(e) =>
              onChange('questionConfig.additionalPrompt', e.target.value)
            }
            rows={4}
            placeholder="You shall ask the questions one by one..."
          />
        </Field>
        <div className="col gap-2">
          <label className="field-label">Prefill user profile as context</label>
          {interview.prefillAnswers?.map((question, idx) => (
            <div key={idx} className="row gap-2">
              <Input
                size="sm"
                className="grow"
                value={question[language] || ''}
                onChange={(e) => onChangePrefill(idx, e.target.value)}
                placeholder="My name is {profile.name}."
              />
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                title="Delete"
                onClick={() => onDelelePrefill(idx)}
              >
                <Icon name="trash" size={14} />
              </Button>
            </div>
          ))}
          <Button
            variant="ghost"
            size="sm"
            leftIcon="plus"
            onClick={() =>
              onChangePrefill(interview.prefillAnswers?.length || 0, '')
            }
          >
            Add prefill
          </Button>
        </div>
        <div
          className="row gap-4"
          style={{ justifyContent: 'space-between', alignItems: 'center' }}
        >
          <label className="field-label" style={{ margin: 0 }}>
            Start with random reply
          </label>
          <Switch
            checked={!interview.questionConfig.skipRandomStart}
            onChange={() =>
              onChange(
                'questionConfig.skipRandomStart',
                !interview.questionConfig.skipRandomStart
              )
            }
          />
        </div>
        <div className="col gap-2">
          <label className="field-label">Collect data *</label>
          {interview.collect?.map(([key, value], idx) =>
            renderCollect([key, value], idx)
          )}
          <Button
            variant="ghost"
            size="sm"
            leftIcon="plus"
            onClick={() =>
              onChangeCollect(interview.collect?.length || 0, 'answers', '')
            }
          >
            Add field
          </Button>
        </div>
        <div
          className="row gap-4"
          style={{ justifyContent: 'space-between', alignItems: 'center' }}
        >
          <label className="field-label" style={{ margin: 0 }}>
            Complete with AI comment
          </label>
          <Switch
            checked={!!interview.questionConfig.completeWithComment}
            onChange={() =>
              onChange(
                'questionConfig.completeWithComment',
                !interview.questionConfig.completeWithComment
              )
            }
          />
        </div>
      </div>
    );

    /* ---- Selection / Multiple ---- */
    const renderSelection = () => {
      const selectedOption: Partial<OptionEntity> = interview.optionId
        ? optionEntities[interview.optionId]
        : {};
      let hasOther = false;
      if (selectedOption.labels?.length) {
        hasOther = selectedOption.labels.some((option) => option[0] === 0);
      }
      return (
        <div className="col gap-3">
          <div className="row gap-4" style={{ alignItems: 'center' }}>
            <label className="field-label" style={{ margin: 0 }}>
              Options
            </label>
            <Dropdown
              trigger={
                <Button variant="secondary" size="sm" rightIcon="chevron-down">
                  {selectedOption?.field || 'Select option'}
                </Button>
              }
              align="right"
              width={200}
            >
              {Object.entries(optionEntities).map(([key, value]) => (
                <MenuItem
                  key={key}
                  onClick={() =>
                    onBulkChange({
                      optionId: Number(key),
                      collect: [[value.field, undefined, undefined]],
                    })
                  }
                >
                  {value.field}
                </MenuItem>
              ))}
            </Dropdown>
          </div>
          <div className="row gap-4" style={{ alignItems: 'center' }}>
            <label className="field-label" style={{ margin: 0 }}>
              Save To
            </label>
            <ProfileFieldSelector
              value={interview.collect[0]?.[0] || ''}
              onChange={(val: string) => onChangeCollect(0, val)}
            />
          </div>
          {hasOther && (
            <div className="col gap-2" style={{ marginTop: 4 }}>
              <div
                className="row gap-4"
                style={{
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <label className="field-label" style={{ margin: 0 }}>
                  Allow Other Input
                </label>
                <Switch
                  checked={!interview.questionConfig.disableOther}
                  onChange={() =>
                    onChange(
                      'questionConfig.disableOther',
                      !interview.questionConfig.disableOther
                    )
                  }
                />
              </div>
              {!interview.questionConfig.disableOther && (
                <p
                  style={{
                    fontSize: 'var(--fs-sm)',
                    color: 'var(--text-secondary)',
                  }}
                >
                  Other is saved into{' '}
                  <strong>answers.{selectedOption?.field}_other</strong>
                </p>
              )}
            </div>
          )}
        </div>
      );
    };

    /* ---- Dropdown ---- */
    const renderDropdown = () => {
      const selectedOption: Partial<OptionEntity> = interview.optionId
        ? optionEntities[interview.optionId]
        : {};
      const selectedLabel = selectedOption.labels?.find(
        (label) => label[0] === interview.questionConfig.default1
      );
      return (
        <div className="col gap-3">
          <div className="row gap-4" style={{ alignItems: 'center' }}>
            <label className="field-label" style={{ margin: 0 }}>
              Options
            </label>
            <Dropdown
              trigger={
                <Button variant="secondary" size="sm" rightIcon="chevron-down">
                  {selectedOption?.field || 'Select option'}
                </Button>
              }
              align="right"
              width={200}
            >
              {Object.entries(optionEntities).map(([key, value]) => (
                <MenuItem
                  key={key}
                  onClick={() =>
                    onBulkChange({
                      optionId: Number(key),
                      collect: [[value.field, undefined, undefined]],
                    })
                  }
                >
                  {value.field}
                </MenuItem>
              ))}
            </Dropdown>
          </div>
          <div className="row gap-4" style={{ marginTop: 4 }}>
            <Field label="Save To">
              <ProfileFieldSelector
                value={interview.collect[0]?.[0] || ''}
                onChange={(val: string) => onChangeCollect(0, val)}
              />
            </Field>
            <Field label="Default">
              <Dropdown
                trigger={
                  <Button
                    variant="secondary"
                    size="sm"
                    rightIcon="chevron-down"
                  >
                    {selectedLabel?.[1][language] || 'Select default'}
                  </Button>
                }
                align="right"
                width={200}
              >
                <MenuItem
                  onClick={() => onChange('questionConfig.default1', undefined)}
                >
                  None
                </MenuItem>
                {selectedOption.labels?.map((label) => (
                  <MenuItem
                    key={label[0]}
                    active={label[0] === interview.questionConfig.default1}
                    onClick={() =>
                      onChange('questionConfig.default1', label[0])
                    }
                  >
                    {label[1][language]}
                  </MenuItem>
                ))}
              </Dropdown>
            </Field>
          </div>
        </div>
      );
    };

    /* ---- Range ---- */
    const renderRange = () => {
      const selectedOption: Partial<OptionEntity> = interview.optionId
        ? optionEntities[interview.optionId]
        : {};
      const selectedLabel1 = selectedOption.labels?.find(
        (label) => label[0] === interview.questionConfig.default1
      );
      const selectedLabel2 = selectedOption.labels?.find(
        (label) => label[0] === interview.questionConfig.default2
      );

      return (
        <div className="col gap-3">
          <div className="row gap-4" style={{ alignItems: 'center' }}>
            <label className="field-label" style={{ margin: 0 }}>
              Options
            </label>
            <Dropdown
              trigger={
                <Button variant="secondary" size="sm" rightIcon="chevron-down">
                  {selectedOption?.field || 'Select option'}
                </Button>
              }
              align="right"
              width={200}
            >
              {Object.entries(optionEntities).map(([key, value]) => (
                <MenuItem
                  key={key}
                  onClick={() =>
                    onBulkChange({
                      optionId: Number(key),
                      collect: [
                        [`${value.field}Min`, undefined, undefined],
                        [`${value.field}Max`, undefined, undefined],
                      ],
                    })
                  }
                >
                  {value.field}
                </MenuItem>
              ))}
            </Dropdown>
          </div>
          <div className="row gap-4" style={{ marginTop: 4 }}>
            <Field label="Save Min">
              <ProfileFieldSelector
                value={interview.collect[0]?.[0] || ''}
                onChange={(val: string) => onChangeCollect(0, val)}
              />
            </Field>
            <Field label="Default Min">
              <Dropdown
                trigger={
                  <Button
                    variant="secondary"
                    size="sm"
                    rightIcon="chevron-down"
                  >
                    {selectedLabel1?.[1][language] || 'Select default'}
                  </Button>
                }
                align="right"
                width={200}
              >
                <MenuItem
                  onClick={() => onChange('questionConfig.default1', undefined)}
                >
                  None
                </MenuItem>
                {selectedOption.labels?.map((label) => (
                  <MenuItem
                    key={label[0]}
                    active={label[0] === interview.questionConfig.default1}
                    onClick={() =>
                      onChange('questionConfig.default1', label[0])
                    }
                  >
                    {label[1][language]}
                  </MenuItem>
                ))}
              </Dropdown>
            </Field>
          </div>
          <div className="row gap-4">
            <Field label="Save Max">
              <ProfileFieldSelector
                value={interview.collect[1]?.[0] || ''}
                onChange={(val: string) => onChangeCollect(1, val)}
              />
            </Field>
            <Field label="Default Max">
              <Dropdown
                trigger={
                  <Button
                    variant="secondary"
                    size="sm"
                    rightIcon="chevron-down"
                  >
                    {selectedLabel2?.[1][language] || 'Select default'}
                  </Button>
                }
                align="right"
                width={200}
              >
                <MenuItem
                  onClick={() => onChange('questionConfig.default1', undefined)}
                >
                  None
                </MenuItem>
                {selectedOption.labels?.map((label) => (
                  <MenuItem
                    key={label[0]}
                    active={label[0] === interview.questionConfig.default2}
                    onClick={() =>
                      onChange('questionConfig.default2', label[0])
                    }
                  >
                    {label[1][language]}
                  </MenuItem>
                ))}
              </Dropdown>
            </Field>
          </div>
        </div>
      );
    };

    /* ---- Single collect (date, etc.) ---- */
    const renderOneCollect = () => (
      <div className="row gap-4" style={{ alignItems: 'center' }}>
        <label className="field-label" style={{ margin: 0 }}>
          Save To
        </label>
        <ProfileFieldSelector
          value={interview.collect[0]?.[0] || ''}
          onChange={(val: string) => onChangeCollect(0, val)}
        />
      </div>
    );

    /* ---- Confirm ---- */
    const renderConfirm = () => {
      const stopCollect = interview.collect?.[0] || ['stop', {}];
      const continueCollect = interview.collect?.[1] || ['continue', {}];
      return (
        <div className="col gap-3" style={{ marginTop: 4 }}>
          <div className="row gap-4" style={{ alignItems: 'center' }}>
            <label className="field-label" style={{ margin: 0, flexShrink: 0 }}>
              Stop Label
            </label>
            <Input
              size="sm"
              className="grow"
              value={stopCollect[1]?.[language] || ''}
              onChange={(e) =>
                onChangeCollect(0, stopCollect[0], e.target.value)
              }
              placeholder="E.g. Take a break"
            />
          </div>
          <div className="row gap-4" style={{ alignItems: 'center' }}>
            <label className="field-label" style={{ margin: 0, flexShrink: 0 }}>
              Continue Label
            </label>
            <Input
              size="sm"
              className="grow"
              value={continueCollect[1]?.[language] || ''}
              onChange={(e) =>
                onChangeCollect(1, continueCollect[0], e.target.value)
              }
              placeholder="E.g. Continue"
            />
          </div>
          <div className="row gap-4" style={{ alignItems: 'center' }}>
            <label className="field-label" style={{ margin: 0, flexShrink: 0 }}>
              Current Stage
            </label>
            <Input
              size="sm"
              className="grow"
              value={interview.questionConfig.default1 ?? ''}
              onChange={(e) =>
                onChange('questionConfig.default1', Number(e.target.value))
              }
              placeholder="E.g. 1,2,3... -1 means completion of all"
            />
          </div>
        </div>
      );
    };

    const renderConfigs = () => {
      switch (interview.type) {
        case INTERVIEW_TYPE.AI:
          return renderAiConfig();
        case INTERVIEW_TYPE.CONFIRM:
          return renderConfirm();
        case INTERVIEW_TYPE.SINGLE_SELECTION:
        case INTERVIEW_TYPE.MULTIPLE_SELECTION:
          return renderSelection();
        case INTERVIEW_TYPE.DROPDOWN:
          return renderDropdown();
        case INTERVIEW_TYPE.RANGE_SELECTION:
          return renderRange();
        case INTERVIEW_TYPE.DATE:
          return renderOneCollect();
        default:
          return null;
      }
    };

    return (
      <Panel
        className="grow"
        title={interview.id ? 'Edit' : 'Create'}
        icon="edit"
        scroll
        headerRight={
          <div className="row gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={onDuplicate}
              title="Duplicate"
            >
              Duplicate
            </Button>
            <Button variant="secondary" size="sm" onClick={onCreate}>
              New
            </Button>
            <label
              className="row gap-2"
              style={{
                fontSize: 'var(--fs-xs)',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
              }}
            >
              <Switch
                size="sm"
                checked={!interview.disabled}
                onChange={(checked) => onChange('disabled', !checked)}
              />
              {interview.disabled ? 'Disabled' : 'Enabled'}
            </label>
          </div>
        }
      >
        <div className="col gap-3">
          {/* Sequence */}
          <div
            className="row gap-4"
            style={{ justifyContent: 'space-between', alignItems: 'center' }}
          >
            <label className="field-label" style={{ margin: 0 }}>
              Sequence *
            </label>
            <Input
              size="sm"
              style={{ width: 120 }}
              value={interview.sequence || ''}
              type="number"
              onChange={(e) => onChange('sequence', Number(e.target.value))}
              placeholder="1,2,3..."
            />
          </div>

          {/* Name */}
          <div className="col gap-1">
            <div
              className="row gap-4"
              style={{ justifyContent: 'space-between', alignItems: 'center' }}
            >
              <label className="field-label" style={{ margin: 0 }}>
                Name *
              </label>
              <Input
                size="sm"
                style={{ flex: 1 }}
                value={interview.name}
                onChange={(e) => onChange('name', e.target.value)}
                placeholder="use_underscores_and_lowercase"
              />
            </div>
            {errors.name && (
              <span
                style={{
                  fontSize: 'var(--fs-xs)',
                  color: 'var(--red-text)',
                }}
              >
                {errors.name}
              </span>
            )}
          </div>

          {/* Type */}
          <div
            className="row gap-4"
            style={{ justifyContent: 'space-between', alignItems: 'center' }}
          >
            <label className="field-label" style={{ margin: 0 }}>
              Type
            </label>
            <Select
              size="sm"
              value={interview.type}
              onChange={(e) => onChange('type', Number(e.target.value))}
            >
              {Object.values(INTERVIEW_TYPE)
                .filter((v) => typeof v === 'number')
                .map((type) => (
                  <option key={type} value={type as number}>
                    {INTERVIEW_TYPE_STR[type as number]}
                  </option>
                ))}
            </Select>
          </div>

          {/* Section ID */}
          <div
            className="row gap-4"
            style={{ justifyContent: 'space-between', alignItems: 'center' }}
          >
            <label className="field-label" style={{ margin: 0 }}>
              Section ID *
            </label>
            <Select
              size="sm"
              value={interview.sectionId}
              onChange={(e) => onChange('sectionId', Number(e.target.value))}
            >
              {[0, 1, 2, 3, 4].map((sid) => (
                <option key={sid} value={sid}>
                  {sid}
                </option>
              ))}
            </Select>
          </div>

          {/* Condition */}
          <div
            style={{
              borderTop: '1px solid var(--border)',
              paddingTop: 12,
            }}
          >
            {renderCondition()}
          </div>

          {/* Summary + language */}
          <div
            className="row gap-4"
            style={{ justifyContent: 'space-between', alignItems: 'center' }}
          >
            <span
              style={{
                fontSize: 'var(--fs-xs)',
                color: 'var(--text-secondary)',
                fontWeight: 500,
              }}
            >
              Language: Chinese
            </span>
            <label
              className="row gap-2"
              style={{
                fontSize: 'var(--fs-xs)',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
              }}
            >
              <Switch
                size="sm"
                checked={!!interview.useAsSummary}
                onChange={(checked) => onChange('useAsSummary', checked)}
              />
              Public Summary
            </label>
          </div>

          {/* Question text */}
          <Field label="Question">
            <Textarea
              value={interview.question[language] || ''}
              onChange={(e) => onChange(`question.${language}`, e.target.value)}
              placeholder="Use {} to substitute with user data. E.g. {name} {basics.education_other}"
            />
          </Field>

          {/* Placeholder */}
          <div
            className="row gap-4"
            style={{ justifyContent: 'space-between', alignItems: 'center' }}
          >
            <label className="field-label" style={{ margin: 0 }}>
              Placeholder
            </label>
            <Input
              size="sm"
              style={{ flex: 1 }}
              value={interview.placeholder[language] || ''}
              onChange={(e) =>
                onChange(`placeholder.${language}`, e.target.value)
              }
              placeholder="E.g. Others, please specify"
            />
          </div>

          {/* Type-specific config */}
          <div
            style={{
              borderTop: '1px solid var(--border)',
              paddingTop: 12,
            }}
          >
            {renderConfigs()}
          </div>

          {/* Actions */}
          <div
            className="row gap-2"
            style={{
              borderTop: '1px solid var(--border)',
              paddingTop: 12,
              marginTop: 4,
            }}
          >
            <Button
              variant="primary"
              size="sm"
              className="grow"
              leftIcon="save"
              disabled={!isDirty.current}
              loading={isCreating || isUpdating}
              onClick={onSave}
            >
              Save
            </Button>
            {interview.id > 0 && (
              <Button
                variant="secondary"
                size="sm"
                leftIcon="play"
                onClick={() => onTestInterview(interview.id)}
              >
                Test
              </Button>
            )}
            {interview.id > 0 && (
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                title="Delete interview"
                loading={isDeleting}
                onClick={() => {
                  if (
                    window.confirm(
                      'Are you sure you want to delete this interview? This action is irreversible.'
                    )
                  ) {
                    onDelete();
                  }
                }}
              >
                <Icon name="trash" size={15} />
              </Button>
            )}
          </div>
        </div>
      </Panel>
    );
  }
);

export default Editor;
